import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Button,
  CircularProgress,
  Box,
  Typography,
  FormLabel,
  Grid,
  TextField,
} from "@material-ui/core";
import MmsApiService from "../../../../Services/MMS/mms-api.service";
import useTranslation from "../../../../Hooks/useTranslation";
import AlertToastr from "../../../../utils/alert";
import { Autocomplete } from "@material-ui/lab";

const HealthCheckSettings = ({ open, handleClose, componentId }) => {
  const translation = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [channelMappings, setChannelMappings] = useState(null);
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState({});

  const fetchChannelMappings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await MmsApiService.getChannelMappings(componentId);

      setChannelMappings(response.data.mappings);
      setChannels(response.data.channels);
  
      const initialSelections = {};
      response.data.mappings.forEach((mapping) => {
        const correspondingChannel = response.data.channels.find(
          (ch) => ch.name === mapping.channelNameUser
        );
        if (correspondingChannel) {
          initialSelections[mapping.id] = correspondingChannel.name;
        }
      });
      setSelectedChannels(initialSelections);
    } catch (err) {
      console.error("Error fetching channel mappings:", err);
      setError(
        err.response?.data?.message || "Failed to fetch channel mappings"
      );
    } finally {
      setIsLoading(false);
    }
  }, [componentId]);

  useEffect(() => {
    if (open && componentId) {
      fetchChannelMappings();
    }
  }, [open, componentId, fetchChannelMappings]);

  const handleCloseModal = () => {
    setChannelMappings(null);
    setChannels([]);
    setError(null);
    setSelectedChannels({});
    handleClose();
  };

  const handleChannelChange = (channelId, newValue) => {
    setSelectedChannels((prev) => ({
      ...prev,
      [channelId]: newValue,
    }));
  };

  const handleSave = async () => {
    if (!channelMappings || !channels) return;

    setIsSaving(true);
    setError(null);

    try {
      const mappings = channelMappings.map((mapping) => {
        const selectedChannel = channels.find(
          (ch) => ch.name === selectedChannels[mapping.id]
        );
        
        return {
          mappingId: mapping.id,
          channelName: selectedChannel ? selectedChannel.code : mapping.channelName,
          channelNameUser: selectedChannel ? selectedChannel.name : mapping.channelNameUser,
        };
      });

      const requestBody = {
        mappings: mappings,
      };
      
      
      await MmsApiService.updateChannelMappings(componentId, requestBody);
     
      handleCloseModal();
      AlertToastr.showAlert("Channel mappings have been successfully updated");
    } catch (err) {
      console.error("Error updating channel mappings:", err);
      setError(
        err.response?.data?.message || "Failed to update channel mappings"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const metricChannels =
    channelMappings?.filter((channel) => !channel.isConditionalChannel) || [];
  const conditionalChannels =
    channelMappings?.filter((channel) => channel.isConditionalChannel) || [];

  return (
    <Dialog
      open={open}
      className="respModal scrollContentModal calendarModal autoHeight datepicker-modal"
    >
      <form action="">
        <div className="formContentHolder">
          <div className="modalTitleHolder">
            <h2>Health Check Settings</h2>
          </div>

          <div className="formContent">
            <div className="formContentBlock">
              {isLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error">Error: {error}</Typography>
              ) : (
                <Box>
                  {/* Metric channels section */}
                  {metricChannels.length > 0 && (
                    <Box mb={4} mt={2}>
                      <FormLabel
                        component="legend"
                        style={{
                          fontWeight: "bold",
                          marginBottom: "24px",
                          display: "block",
                        }}
                      >
                        Metric channels
                      </FormLabel>

                      {metricChannels.map((channel) => (
                        <Grid
                          container
                          spacing={2}
                          key={channel.id}
                          style={{ marginBottom: "16px" }}
                        >
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              readOnly
                              value={channel.metricChannelName}
                              label="Metric Channel Name"
                              onFocus={(e) => e.target.blur()}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Autocomplete
                              options={channels}
                              getOptionLabel={(option) => 
                                option ? `${option.code} | ${option.name.slice(0, 25)}...` : ''
                              }
                              value={channels.find(
                                (ch) => ch.name === (selectedChannels[channel.id] || channel.channelNameUser)
                              ) || null}
                              onChange={(event, newValue) =>
                                handleChannelChange(
                                  channel.id,
                                  newValue ? newValue.name : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Channel name"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                              renderOption={(option) => (
                                <div>
                                  {option.code} | {option.name}
                                </div>
                              )}
                              ListboxProps={{
                                style: {
                                  maxHeight: 300,
                                },
                              }}
                              isOptionEqualToValue={(option, value) => 
                                option?.name === value?.name
                              }
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  )}

                  {/* Metric condition channels section */}
                  {conditionalChannels.length > 0 && (
                    <Box>
                      <FormLabel
                        component="legend"
                        style={{
                          fontWeight: "bold",
                          marginBottom: "16px",
                          display: "block",
                        }}
                      >
                        Metric condition channels
                      </FormLabel>

                      {conditionalChannels.map((channel) => (
                        <Grid
                          container
                          spacing={2}
                          key={channel.id}
                          style={{ marginBottom: "16px" }}
                        >
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              readOnly
                              value={channel.metricChannelName}
                              label="Metric Channel Name"
                              onFocus={(e) => e.target.blur()}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Autocomplete
                              options={channels}
                              getOptionLabel={(option) => 
                                option ? `${option.code} | ${option.name.slice(0, 25)}...` : ''
                              }
                              value={channels.find(
                                (ch) => ch.name === (selectedChannels[channel.id] || channel.channelNameUser)
                              ) || null}
                              onChange={(event, newValue) =>
                                handleChannelChange(
                                  channel.id,
                                  newValue ? newValue.name : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Channel name"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                              renderOption={(option) => (
                                <div>
                                  {option.code} | {option.name}
                                </div>
                              )}
                              ListboxProps={{
                                style: {
                                  maxHeight: 300,
                                },
                              }}
                              isOptionEqualToValue={(option, value) => 
                                option?.name === value?.name
                              }
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  )}

                  {metricChannels.length === 0 &&
                    conditionalChannels.length === 0 && (
                      <Typography variant="body2" color="textSecondary">
                        No channel mappings found for this component.
                      </Typography>
                    )}
                </Box>
              )}
            </div>
          </div>

          <div className="btnHolder fixedWidthBtn">
            <Button onClick={handleCloseModal} variant="outlined">
              {translation.cancelButtonLabel}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isLoading || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </form>

      <style>{`
        /* Autocomplete specific styles to match select dropdown */
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          min-height: 35px !important;
          height: 35px !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          padding: 8.5px 14px !important;
          line-height: 1.1876em !important;
          height: auto !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input::placeholder {
          line-height: 1.1876em !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-marginDense {
          min-height: 35px !important;
          height: 35px !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-root {
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
        }
        
        .MuiAutocomplete-root .MuiOutlinedInput-input {
          margin: 0 !important;
          padding: 8.5px 14px !important;
        }
        
        .MuiAutocomplete-listbox {
          padding: 0 !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option {
          padding: 4px 8px !important;
          min-height: auto !important;
          font-size: 13px !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option:hover {
          background-color: rgba(0, 0, 0, 0.04) !important;
        }
        
        .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"] {
          background-color: rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Checkbox styling in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root {
          padding: 4px !important;
          margin-right: 6px !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root .MuiSvgIcon-root {
          font-size: 18px !important;
        }
        
        /* Ensure consistent spacing */
        .MuiAutocomplete-listbox .MuiAutocomplete-option > div {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }
        
        /* Checkbox colors in autocomplete */
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked svg {
          color: #006ae7 !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root.Mui-checked path {
          fill: #006ae7 !important;
        }
        
        /* Keep unchecked checkboxes in their original color */
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) svg {
          color: inherit !important;
        }
        
        .MuiAutocomplete-listbox .MuiCheckbox-root:not(.Mui-checked) path {
          fill: inherit !important;
        }
      `}</style>
    </Dialog>
  );
};

export default HealthCheckSettings;
